// Haversine distance in meters
function haversineMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Find the nearest campus to a GPS position (within maxDist meters)
function nearestCampus(lat, lng, campuses, maxDist = 5000) {
    let best = null;
    let bestDist = Infinity;
    for (const campus of campuses || []) {
        const distance = haversineMeters(lat, lng, campus.lat, campus.lng);
        if (distance < bestDist) {
            bestDist = distance;
            best = campus;
        }
    }
    return bestDist <= maxDist ? best : null;
}

export function combineReListings(reListings, nearbyVisitProperties) {
    const seen = new Set();
    const merged = [];
    for (const item of reListings) {
        if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
        }
    }
    for (const item of nearbyVisitProperties) {
        if (!seen.has(item.id)) {
            seen.add(item.id);
            merged.push({ ...item, nearVisit: true });
        }
    }
    return merged;
}

export function getMapChannels({
    showRealEstate,
    showPlaces,
    combinedReListings,
    placesResults,
    localSearchResults,
    publicChannels = [],
    search,
    filteredResults,
    isEvents,
    chatroomEvents,
}) {
    if (showRealEstate) {
        return combinedReListings.map((listing) => ({
            id: listing.id,
            title: listing.address || '(no address)',
            subTitle: [listing.city, listing.state, listing.postalCode].filter(Boolean).join(', '),
            addresses: [{ latitude: listing.latitude, longitude: listing.longitude }],
            discoverySource: listing.discoverySource || null,
            discoveryContext: listing.discoveryContext || null,
        }));
    }
    if (showPlaces) {
        return placesResults.map((place) => ({
            id: place.id,
            title: place.name || '(unnamed)',
            subTitle: place.formattedAddress || '',
            addresses: [{ latitude: place.location?.latitude, longitude: place.location?.longitude }],
            // Pass through team data for local channel matches so
            // resolvePin can render the team avatar pin
            teamData: place.teamData,
            premiumPinCategory: place.premiumPinCategory,
            normalPinCategory: place.normalPinCategory,
            activeEvents: place.activeEvents,
            accessibility: place.accessibility,
            matchedEventIcon: place.matchedEventIcon,
        }));
    }
    // Local search results (near me / campus) — show with team avatar pins
    if (localSearchResults.length > 0) {
        return localSearchResults;
    }
    // Regular text search — filter public channels by title
    if (search && filteredResults.length > 0) {
        return filteredResults;
    }
    // When public tasks are selected, show the public channels those tasks belong to.
    if (isEvents && chatroomEvents?.length > 0) {
        const taskChannelIds = new Set(
            chatroomEvents.flatMap((event) => {
                const assignedChannels = Array.isArray(event?.assignedChannel)
                    ? event.assignedChannel
                    : event?.assignedChannel
                      ? [event.assignedChannel]
                      : [];
                return assignedChannels
                    .map((channel) => (typeof channel === 'object' ? channel?._id || channel?.id : channel))
                    .filter(Boolean)
                    .map(String);
            })
        );
        return publicChannels.filter((channel) => taskChannelIds.has(String(channel?._id || channel?.id)));
    }
    // Default map view shows every public channel with a location.
    return publicChannels;
}

export function createGeoLocationSearchHandlers(dependencies) {
    const {
        eventTypes,
        publicChannels,
        campuses,
        pos,
        inputRef,
        mapIntentRequestIdRef,
        placesSearchApi,
        toastWrapper,
        setPlacesResults,
        setSelectedPlace,
        setPlacesLoading,
        setActiveChannel,
        setSearch,
        setLocalSearchResults,
        setFilteredResults,
        showRealEstate,
        setReFilters,
        setReBobConfirm,
        loadListings,
        handleSearch,
        showPlaces,
        setShowRealEstate,
        setShowPlaces,
    } = dependencies;

    /**
     * Match search text keywords to event type titles (case-insensitive).
     * Returns the first matching event type's image URL, or null.
     */
    const matchEventIcon = (searchText) => {
        if (!searchText || !eventTypes?.length) return null;
        const keywords = searchText
            .toLowerCase()
            .split(/\s+/)
            .filter((w) => w.length >= 3);
        for (const kw of keywords) {
            const match = eventTypes.find((et) => (et?.title || '').toLowerCase().includes(kw));
            if (match?.image) return match.image;
        }
        return null;
    };

    // Places search handler — uses the unified map-intent result, then searches Places.
    // Also searches local public channels and merges them at the top of results.
    const handlePlacesSearch = async (text, mapIntentResult = null) => {
        if (!text || !text.trim()) {
            setPlacesResults([]);
            setSelectedPlace(null);
            return;
        }
        setPlacesLoading(true);
        // Compute local channel matches outside try/catch so catch can still show them
        const searchLower = text.toLowerCase().trim();

        // Semantic campus/university keywords — for detecting intent even without text match
        const UNIVERSITY_TYPES = ['university', 'campus', 'college'];
        // Campus name aliases — so "upenn" matches "university of pennsylvania" etc
        const CAMPUS_ALIASES = {
            'university of pennsylvania': ['upenn', 'penn', 'u penn'],
            'drexel university': ['drexel'],
            'temple university': ['temple'],
        };
        // Extract significant words from query (≥3 chars) for campus name matching
        const searchWords = searchLower.split(/\s+/).filter((w) => w.length >= 3);

        const localMatches = publicChannels
            .filter((ch) => {
                const title = (ch.title || '').toLowerCase();
                const sub = (ch.subTitle || '').toLowerCase();
                const campusName = (ch.campusName || '').toLowerCase();
                const placeCategory = (ch.placeCategory || '').toLowerCase();

                // Check alias match — "upenn" -> matches campusName "university of pennsylvania"
                const aliasMatch = Object.entries(CAMPUS_ALIASES).some(
                    ([canonical, aliases]) =>
                        campusName.includes(canonical) && aliases.some((a) => searchLower.includes(a))
                );
                // 1. Direct text match on title/subTitle
                const textMatch =
                    title.includes(searchLower) ||
                    sub.includes(searchLower) ||
                    searchLower.includes(title) ||
                    title.split(/\s+/).some((w) => searchLower.includes(w) && w.length > 3);

                // 2. Semantic match: if query contains campus/university keywords,
                //    surface all channels tagged as university/campus buildings,
                //    especially if the campus name appears anywhere in the query
                const querySuggestsUniversity = UNIVERSITY_TYPES.some((t) => searchLower.includes(t));
                const campusNameMatch = campusName && searchWords.some((w) => campusName.includes(w));
                const categoryMatch = UNIVERSITY_TYPES.includes(placeCategory);
                const semanticMatch = querySuggestsUniversity && (campusNameMatch || categoryMatch);

                // 3. Campus name in query even without "campus" keyword (e.g. "upenn hall")
                const implicitCampusMatch = campusName && campusNameMatch;

                return textMatch || semanticMatch || implicitCampusMatch || aliasMatch;
            })
            .map((ch) => ({
                id: ch._id,
                name: ch.title,
                formattedAddress: ch.subTitle || '',
                location: {
                    latitude: ch.addresses?.[0]?.latitude || ch.latitude,
                    longitude: ch.addresses?.[0]?.longitude || ch.longitude,
                },
                _isLocalChannel: true,
                _channelId: ch._id,
                // Preserve team data so resolvePin can render the team avatar
                teamData: ch.teamData,
                premiumPinCategory: ch.premiumPinCategory,
                normalPinCategory: ch.normalPinCategory,
                activeEvents: ch.activeEvents,
                accessibility: ch.accessibility,
                matchedEventIcon: matchEventIcon(text),
            }));
        try {
            // 1. Local channel matches computed above

            const lat = pos?.coords?.latitude;
            const lng = pos?.coords?.longitude;

            // If no geolocation is available and we have local channel matches,
            // show them immediately without hitting the external API
            if (!lat && !lng && localMatches.length > 0) {
                setPlacesResults(localMatches);
                return;
            }

            const parsed = mapIntentResult?.intent || (mapIntentResult?.category === 'places' ? {} : null);

            // If the parser says this is not a places query AND we have local matches,
            // show local channels only (don't hit external API)
            if (!parsed) {
                if (localMatches.length > 0) {
                    setPlacesResults(localMatches);
                    return;
                }
                setActiveChannel(null);
                setSearch(inputRef.current.value.toLowerCase());
                return;
            }

            const res = await placesSearchApi.searchPlaces({
                query: parsed.query || text,
                lat,
                lng,
                geocodedLat: parsed.geocodedLat,
                geocodedLng: parsed.geocodedLng,
                minPrice: parsed.minPrice,
                maxPrice: parsed.maxPrice,
                limit: 20,
                placeTypes: parsed.placeTypes,
                genericCampus: parsed.genericCampus ? 'true' : undefined,
            });
            // Merge: local channels first, then external places
            const externalPlaces = (res?.places || []).map((p) => ({
                ...p,
                _isLocalChannel: false,
                matchedEventIcon: matchEventIcon(text),
            }));
            setPlacesResults([...localMatches, ...externalPlaces]);
        } catch (e) {
            // Don't toast if we have local channel matches to show
            if (!localMatches || !localMatches.length) {
                toastWrapper.error(e?.message || 'Places search failed');
                setPlacesResults([]);
            } else {
                setPlacesResults(localMatches);
            }
        } finally {
            setPlacesLoading(false);
        }
    };

    // ─── Local channel search (near me / campus) ───
    // Returns Optonome building channels with team avatar pins — NOT Google Places.
    const handleLocalSearch = (searchValue, campusKey = null, searchType = null) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;

        // 1. "near me" — show public channels within 5km of user GPS
        if (searchType === 'near_me') {
            if (!lat || !lng) {
                toastWrapper.info('Enable location to find channels near you');
                setLocalSearchResults([]);
                return;
            }
            const nearby = publicChannels
                .filter((ch) => {
                    const addr = ch.addresses?.[0];
                    if (!addr?.latitude || !addr?.longitude) return false;
                    return haversineMeters(lat, lng, addr.latitude, addr.longitude) <= 5000;
                })
                .map((ch) => ({
                    id: ch._id,
                    title: ch.title,
                    subTitle: ch.subTitle || '',
                    addresses: [{ latitude: ch.addresses?.[0]?.latitude, longitude: ch.addresses?.[0]?.longitude }],
                    teamData: ch.teamData,
                    premiumPinCategory: ch.premiumPinCategory,
                    normalPinCategory: ch.normalPinCategory,
                    activeEvents: ch.activeEvents,
                    accessibility: ch.accessibility,
                    matchedEventIcon: matchEventIcon(searchValue),
                }));
            setLocalSearchResults(nearby);

            return;
        }

        // 2. Specific campus ("drexel campus", "penn campus", etc.)
        const namedCampus = (campuses || []).find((campus) => campus.key === campusKey);
        if (namedCampus) {
            const campusChannels = publicChannels
                .filter((ch) => {
                    const addr = ch.addresses?.[0];
                    if (!addr?.latitude || !addr?.longitude) return false;
                    // Within 2km of campus center
                    return haversineMeters(namedCampus.lat, namedCampus.lng, addr.latitude, addr.longitude) <= 2000;
                })
                .map((ch) => ({
                    id: ch._id,
                    title: ch.title,
                    subTitle: ch.subTitle || '',
                    addresses: [{ latitude: ch.addresses?.[0]?.latitude, longitude: ch.addresses?.[0]?.longitude }],
                    teamData: ch.teamData,
                    premiumPinCategory: ch.premiumPinCategory,
                    normalPinCategory: ch.normalPinCategory,
                    activeEvents: ch.activeEvents,
                    accessibility: ch.accessibility,
                    matchedEventIcon: matchEventIcon(searchValue),
                }));
            setLocalSearchResults(campusChannels);

            return;
        }

        // 3. Generic "campus" — detect which campus the user is ON, show all pins there
        if (searchType === 'generic_campus') {
            if (!lat || !lng) {
                toastWrapper.info('Enable location to find your campus');
                setLocalSearchResults([]);
                return;
            }
            const campus = nearestCampus(lat, lng, campuses, 5000);
            if (!campus) {
                toastWrapper.info('No campus detected within 5km of your location');
                setLocalSearchResults([]);
                return;
            }
            const campusChannels = publicChannels
                .filter((ch) => {
                    const addr = ch.addresses?.[0];
                    if (!addr?.latitude || !addr?.longitude) return false;
                    return haversineMeters(campus.lat, campus.lng, addr.latitude, addr.longitude) <= 2000;
                })
                .map((ch) => ({
                    id: ch._id,
                    title: ch.title,
                    subTitle: ch.subTitle || '',
                    addresses: [{ latitude: ch.addresses?.[0]?.latitude, longitude: ch.addresses?.[0]?.longitude }],
                    teamData: ch.teamData,
                    premiumPinCategory: ch.premiumPinCategory,
                    normalPinCategory: ch.normalPinCategory,
                    activeEvents: ch.activeEvents,
                    accessibility: ch.accessibility,
                    matchedEventIcon: matchEventIcon(searchValue),
                }));
            setLocalSearchResults(campusChannels);

            return;
        }

        // Not a local search query
        setLocalSearchResults([]);
    };

    const handleMapSearch = async () => {
        const regexExp = /[.*+?^$%&@{}()|[\\\\]\\\\\\\\]/g;
        const searchValue = inputRef.current.value;
        const requestId = ++mapIntentRequestIdRef.current;
        if (searchValue.length === 1 && searchValue.match(regexExp)) {
            setSearch('');
        } else if (searchValue.length < 1) {
            setFilteredResults([]);
            setLocalSearchResults([]);

            if (showRealEstate) {
                setReFilters({});
                setReBobConfirm(null);
                loadListings({});
            }
            if (showPlaces) {
                setPlacesResults([]);
                setSelectedPlace(null);
            }
        } else {
            try {
                const lat = pos?.coords?.latitude;
                const lng = pos?.coords?.longitude;
                const mapIntent = await placesSearchApi.resolveMapIntent(searchValue, lat, lng);
                if (requestId !== mapIntentRequestIdRef.current) return;
                setActiveChannel(null);

                if (mapIntent?.category === 'real_estate' || showRealEstate) {
                    setShowPlaces(false);
                    setShowRealEstate(true);
                    handleSearch(searchValue, mapIntent);
                    return;
                }

                if (mapIntent?.category === 'campus') {
                    setShowPlaces(false);
                    setShowRealEstate(false);
                    const searchType = mapIntent?.filters?.genericCampus ? 'generic_campus' : null;
                    handleLocalSearch(searchValue, mapIntent?.filters?.campusKey, searchType);
                    return;
                }

                if (mapIntent?.category === 'near_me') {
                    setShowPlaces(false);
                    setShowRealEstate(false);
                    handleLocalSearch(searchValue, null, 'near_me');
                    return;
                }

                if (mapIntent?.category === 'places' || showPlaces) {
                    setShowRealEstate(false);
                    if (!showPlaces) setShowPlaces(true);
                    handlePlacesSearch(searchValue, mapIntent);
                    return;
                }

                setShowRealEstate(false);
                setShowPlaces(false);
                setSearch(searchValue.toLowerCase());
            } catch (error) {
                if (requestId !== mapIntentRequestIdRef.current) return;
                if (error?.name === 'AbortError') return;
                setActiveChannel(null);
                setShowRealEstate(false);
                setShowPlaces(false);
                setSearch(searchValue.toLowerCase());
            }
        }
    };

    return { handlePlacesSearch, handleLocalSearch, handleMapSearch };
}
